/**
 * Read-only source row for the split-view transcript surface (#2237).
 *
 * Plain-HTML renderer — no Slate editor on the source side. This
 * removes an entire class of bugs where the user types or formats
 * into the source and the shared document changes under every other
 * translator's feet. Text is still selectable (so copy works), each
 * word carries `data-start` / `data-stop` so the comments gutter
 * (#2244) can anchor markers.
 *
 * Decision in-code (#2237): source cells do NOT host playback
 * highlighting. The AC calls for "highlight runs in the right pane"
 * — leaving the markup to pure text avoids the current-word tint
 * from showing on both sides.
 */

import type { CSSProperties } from 'react';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import type {
  DocumentTranscriptParagraphV1,
  DocumentTranscriptTextV1,
  DocumentTranscriptUtteranceV1
} from '@utils/types/editors/transcripts/transcript.ts';
import {
  LeafCommentIcon,
  useLeafCommentAdornments
} from '@components/editors/shared/comments/use-leaf-comment-adornments.tsx';

interface Props {
  utterance: DocumentTranscriptUtteranceV1;
  /** Optional pretty timecode shown in the row header (e.g. "00:12"). */
  timecode?: string;
}

function flattenLeaves(
  paragraphs: DocumentTranscriptParagraphV1[] | undefined
): DocumentTranscriptTextV1[] {
  if (!paragraphs) return [];
  const out: DocumentTranscriptTextV1[] = [];
  for (const p of paragraphs) {
    for (const leaf of (p.children ?? []) as DocumentTranscriptTextV1[]) {
      out.push(leaf);
    }
  }
  return out;
}

// #2246: inline comment adornments on source leaves. The leaf consults
// the nearest `CommentsProvider` — in split view that's the nested
// read-only + 'muted'-tone source provider mounted by
// `SplitTranscriptSurface`, so the underline + icon paint in slate
// instead of amber. Clicking the icon opens the matching flyout card
// in the gutter (read-only; the icon's onClick already handles that).
function SourceLeaf({
  start,
  stop,
  text
}: {
  start: number | undefined;
  stop: number | undefined;
  text: string;
}) {
  const adornments = useLeafCommentAdornments(start, stop);
  const style: CSSProperties = {};
  if (adornments.tint) style.backgroundColor = adornments.tint;
  if (adornments.underlineColor) {
    style.boxShadow = `inset 0 -2px 0 0 ${adornments.underlineColor}`;
  }
  return (
    <span data-start={start} data-stop={stop} style={style}>
      {text}
      <LeafCommentIcon adornments={adornments} />
    </span>
  );
}

export function SourceReadOnlyRow({ utterance, timecode }: Props) {
  const { getColour } = useThemeContext();
  const leaves = flattenLeaves(utterance.children);
  const speakerLabel = utterance.speaker ?? undefined;

  return (
    <article
      // #2244 anchor marker for the comments gutter — matches the
      // live editor row so markers line up with the paragraph.
      data-paragraph-row=""
      className="rounded-2xl border p-2 select-text"
      style={{
        borderColor: getColour('border'),
        backgroundColor: getColour('secondLayer'),
        color: getColour('normalText'),
        cursor: 'text'
      }}
      aria-readonly="true"
    >
      {(speakerLabel || timecode) && (
        <header
          className="flex items-center justify-between text-xs opacity-70 mb-1"
          style={{ color: getColour('inactiveText') }}
        >
          <span className="truncate">{speakerLabel}</span>
          {timecode && (
            <span className="tabular-nums shrink-0">{timecode}</span>
          )}
        </header>
      )}
      <p
        className="text-lg leading-relaxed m-0 whitespace-pre-wrap"
        style={{ color: getColour('normalText') }}
      >
        {leaves.map((leaf, i) => (
          <SourceLeaf
            key={`${leaf.start}-${leaf.stop}-${i}`}
            start={typeof leaf.start === 'number' ? leaf.start : undefined}
            stop={typeof leaf.stop === 'number' ? leaf.stop : undefined}
            text={leaf.text}
          />
        ))}
      </p>
    </article>
  );
}
